import { CryptoService } from './crypto';
import { supabase } from './supabase';

interface MediaCache {
  blob: Blob;
  url: string;
  size: number;
  timestamp: number;
}

interface ChunkMetadata {
  chunk_index: number;
  iv: string;
  sha256_hash: string;
  encrypted_size: number;
}

export class MediaService {
  private static cache = new Map<string, MediaCache>();
  private static readonly MAX_CACHE_SIZE = 50 * 1024 * 1024;
  private static readonly MAX_CACHE_ITEMS = 20;
  private static currentCacheSize = 0;

  static async loadEncryptedMedia(url: string): Promise<string> {
    if (this.cache.has(url)) {
      const cached = this.cache.get(url)!;
      cached.timestamp = Date.now();
      console.log('Serving from cache:', url);
      return cached.url;
    }

    try {
      console.log('Fetching encrypted media:', url);

      const decryptedBlob = await this.downloadAndDecryptChunks(url);
      console.log('Decrypted blob:', decryptedBlob.size, 'bytes');

      const objectUrl = URL.createObjectURL(decryptedBlob);
      console.log('Created object URL:', objectUrl);

      const cacheEntry: MediaCache = {
        blob: decryptedBlob,
        url: objectUrl,
        size: decryptedBlob.size,
        timestamp: Date.now(),
      };

      this.addToCache(url, cacheEntry);

      return objectUrl;
    } catch (error) {
      console.error('Error in loadEncryptedMedia:', error);
      throw error;
    }
  }

  private static addToCache(key: string, entry: MediaCache) {
    while (
      (this.currentCacheSize + entry.size > this.MAX_CACHE_SIZE ||
        this.cache.size >= this.MAX_CACHE_ITEMS) &&
      this.cache.size > 0
    ) {
      this.evictOldest();
    }

    this.cache.set(key, entry);
    this.currentCacheSize += entry.size;
  }

  private static evictOldest() {
    let oldestKey: string | null = null;
    let oldestTimestamp = Date.now();

    for (const [key, entry] of this.cache.entries()) {
      if (entry.timestamp < oldestTimestamp) {
        oldestTimestamp = entry.timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      const entry = this.cache.get(oldestKey)!;
      URL.revokeObjectURL(entry.url);
      this.currentCacheSize -= entry.size;
      this.cache.delete(oldestKey);
    }
  }

  private static async getChunkMetadata(filePath: string): Promise<ChunkMetadata[] | null> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return null;

      const { data, error } = await supabase
        .from('file_chunks')
        .select('chunk_index, iv, sha256_hash, encrypted_size')
        .eq('file_path', filePath)
        .order('chunk_index', { ascending: true });

      if (error) {
        console.error('[MediaService] Error fetching chunk metadata:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('[MediaService] Error in getChunkMetadata:', error);
      return null;
    }
  }

  private static async downloadAndDecryptChunks(url: string): Promise<Blob> {
    const urlParts = url.split('/');
    const fileName = decodeURIComponent(urlParts[urlParts.length - 1]);
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) throw new Error('Not authenticated');

    const filePath = `${user.user.id}/${fileName}`;

    const chunkMetadata = await this.getChunkMetadata(filePath);

    if (!chunkMetadata || chunkMetadata.length === 0) {
      console.log('[MediaService] No chunk metadata found, using legacy decryption');
      const proxyUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/download-from-backblaze?url=${encodeURIComponent(url)}`;

      console.log('[MediaService] Fetching from proxy:', proxyUrl);
      console.log('[MediaService] Original URL:', url);

      const response = await fetch(proxyUrl, {
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
      });

      console.log('[MediaService] Response status:', response.status);
      console.log('[MediaService] Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[MediaService] Download failed:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const encryptedBlob = await response.blob();
      console.log('[MediaService] Downloaded blob size:', encryptedBlob.size, 'type:', encryptedBlob.type);
      return await CryptoService.decryptFile(encryptedBlob);
    }

    console.log(`[MediaService] Found ${chunkMetadata.length} chunks, downloading and decrypting...`);

    const proxyUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/download-from-backblaze?url=${encodeURIComponent(url)}`;

    console.log('[MediaService] Fetching chunked file from proxy:', proxyUrl);

    const response = await fetch(proxyUrl, {
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
    });

    console.log('[MediaService] Chunked response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[MediaService] Chunked download failed:', response.status, errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const encryptedFileBlob = await response.blob();
    const encryptedFileBuffer = await encryptedFileBlob.arrayBuffer();

    const decryptedChunks: ArrayBuffer[] = [];
    let offset = 0;

    const hasEncryptedSize = chunkMetadata.every(meta => meta.encrypted_size > 0);

    if (!hasEncryptedSize) {
      console.log('[MediaService] No encrypted_size in metadata, falling back to legacy method');
      const encryptedBlob = new Blob([encryptedFileBuffer]);
      return await CryptoService.decryptFile(encryptedBlob);
    }

    for (const chunkMeta of chunkMetadata) {
      const chunkSize = chunkMeta.encrypted_size;

      const encryptedChunk = encryptedFileBuffer.slice(offset, offset + chunkSize);

      console.log(`[MediaService] Decrypting chunk ${chunkMeta.chunk_index + 1}/${chunkMetadata.length}, size: ${chunkSize} bytes`);

      const decryptedChunk = await CryptoService.decryptChunk(encryptedChunk, chunkMeta.iv);
      decryptedChunks.push(decryptedChunk);

      offset += chunkSize;
    }

    const totalSize = decryptedChunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
    const combinedBuffer = new Uint8Array(totalSize);
    let writeOffset = 0;

    for (const chunk of decryptedChunks) {
      combinedBuffer.set(new Uint8Array(chunk), writeOffset);
      writeOffset += chunk.byteLength;
    }

    return new Blob([combinedBuffer]);
  }

  static clearCache() {
    for (const entry of this.cache.values()) {
      URL.revokeObjectURL(entry.url);
    }
    this.cache.clear();
    this.currentCacheSize = 0;
  }

  static revokeUrl(url: string) {
    const entry = this.cache.get(url);
    if (entry) {
      URL.revokeObjectURL(entry.url);
      this.currentCacheSize -= entry.size;
      this.cache.delete(url);
    }
  }

  static getCacheSize(): number {
    return this.currentCacheSize;
  }

  static getCacheItemCount(): number {
    return this.cache.size;
  }

  static async downloadEncryptedFile(url: string, filename: string): Promise<void> {
    try {
      console.log('Downloading encrypted file:', url);

      const decryptedBlob = await this.downloadAndDecryptChunks(url);

      console.log('Creating download link...');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(decryptedBlob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      console.log('Download completed:', filename);
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  }
}
